import { Component, DestroyRef, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { BadgeEditorService } from '@app/modules/admin/components/badge-editor/services/badge-editor.service'
import { LandingPageComponent } from '@app/modules/event-landing-page/components/landing-page/landing-page.component'
import { ArchivedItemsService } from '@app/v2/event/services/archived-items.service'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { EMPTY, Observable, Subject, catchError, finalize, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs'
import { ClonePersonaModalComponent } from '../clone-persona-modal/clone-persona-modal.component'
import { CreatePersonaModalComponent } from '../create-persona-modal/create-persona-modal.component'
import { LegacyPersonaWizardContentComponent } from '../legacy-persona-wizard-content/legacy-persona-wizard-content.component'
import { ManagePersonaComponent } from '../manage-persona/manage-persona.component'
import { PersonaModel } from '../models/persona.model'
import { PersonaCardComponent } from '../persona-card/persona-card.component'
import { PersonaBadgePreviewService } from '../services/persona-badge-preview.service'
import { PersonaService } from '../services/persona.service'
import { Persona, PersonaClonedPayload, personaPatchResponse } from '../types/persona.type'

@Component({
  selector: 'app-personas-wizard',
  imports: [PersonaCardComponent, ProgressCircleComponent, NgxSkeletonLoaderModule, CreatePersonaModalComponent, ClonePersonaModalComponent, ModalComponent],
  providers: [PersonaService, BadgeEditorService, PersonaBadgePreviewService, ArchivedItemsService],
  templateUrl: './personas-wizard.component.html',
  styleUrl: './personas-wizard.component.scss'
})
export class PersonasWizardComponent implements OnInit, OnDestroy {
  @Input({ required: true }) eventId!: number
  @Input({ required: true }) organizationId!: number
  @Input({ required: true }) eventSlug!: string
  @Input({ required: true }) eventName!: string

  @ViewChildren(PersonaCardComponent)
  private _personaCards!: QueryList<PersonaCardComponent>

  @ViewChild('loadMoreTrigger')
  set loadMoreTriggerRef(el: ElementRef<HTMLDivElement> | undefined) {
    if (!el) return
    this._observer.disconnect()
    this._observer.observe(el.nativeElement)
  }

  public personas = signal<Persona[]>([])
  public isLoading = signal(false)
  public isCreatingPersona = signal(false)
  public createPersonaApiError = signal<unknown | null>(null)
  public hasPersonaListMutations = signal(false)
  public readonly createPersonaModalId = crypto.randomUUID()
  public readonly clonePersonaModalId = crypto.randomUUID()
  public readonly confirmArchivePersonaModalId = crypto.randomUUID()
  public readonly archivePersonaWarningModalId = crypto.randomUUID()
  public personaNameToArchive = signal('')
  public cloneSourcePersona = signal<Persona | null>(null)
  private _pendingArchivePersonaId = signal<number | null>(null)

  private _page = 1
  private _hasMore = true
  private readonly _perPage = 10

  private readonly _load$ = new Subject<boolean>() // true = reset
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _personaService = inject(PersonaService)
  private readonly _archivedItemsService = inject(ArchivedItemsService)
  private readonly _badgeEditorService = inject(BadgeEditorService)
  private readonly _personaBadgePreviewService = inject(PersonaBadgePreviewService)
  private readonly _modalService = inject(ModalService)
  private readonly _alertService = inject(AlertService)
  private readonly _wizardService = inject(WizardService)

  private _normalizePersonaBadgesForList(personas: Persona[]): Persona[] {
    return (personas ?? []).map((p) => this._normalizePersonaBadgeForList(p))
  }

  private _normalizePersonaBadgeForList(persona: Persona): Persona {
    const html = persona?.badge?.badge_front_html_content
    if (!html || typeof html !== 'string') return persona

    const normalizedHtml = this._personaBadgePreviewService.renderBadgePreviewHtml(html, persona?.name ?? '')
    if (!normalizedHtml || normalizedHtml === html) return persona

    return { ...persona, badge: { ...persona.badge, badge_front_html_content: normalizedHtml } }
  }

  /**
   * Intersection observer to detect when the load more trigger is in view
   * @param {IntersectionObserverEntry[]} entries - The intersection observer entries
   * @param {IntersectionObserver} observer - The intersection observer
   */
  private readonly _observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting && this._hasMore) this._load$.next(false)
    },
    { rootMargin: '200px', threshold: 0.01 }
  )

  ngOnInit(): void {
    this._load$
      .pipe(
        tap((reset) => {
          if (reset) {
            this._page = 1
            this._hasMore = true
            this.personas.set([])
          }
          this.isLoading.set(true)
        }),
        switchMap(() => {
          if (!this._hasMore) return EMPTY
          return this._personaService.getPersonas(this.organizationId, this.eventId, this._perPage, this._page)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: ({ data }) => {
          const rendered = this._normalizePersonaBadgesForList(data)
          this.personas.update((curr) => (this._page === 1 ? rendered : [...curr, ...rendered]))
          this._hasMore = data.length >= this._perPage
          if (this._hasMore) this._page++
          this.isLoading.set(false)
        },
        error: () => {
          this._hasMore = false
          this.isLoading.set(false)
        }
      })

    this._load$.next(true)
  }

  ngOnDestroy(): void {
    this._observer.disconnect()
  }

  /**
   * Used by overlay {@link WizardComponent} to block close when any card has a dirty name field.
   */
  public hasUnsavedChanges = computed(() => this._personaCards?.some((card) => card.hasUnsavedChanges?.()) ?? false)

  /**
   * Clears debounced blur saves before showing the unsaved-changes modal (same contract as event-identity).
   */
  public cancelPendingBlurSave(): void {
    this._personaCards?.forEach((card) => card.cancelPendingBlurSave?.())
  }

  /**
   * Discards in-progress edits on all cards (discard + leave wizard).
   */
  public revertToSaved(): void {
    this._personaCards?.forEach((card) => card.revertToSaved?.())
  }

  /**
   * Saves every card with valid unsaved name changes (unsaved modal — save then leave / stay).
   * @returns {Promise<void>} - The promise to save the unsaved changes
   */
  public saveUnsavedChanges(): Observable<void> {
    const payloads = this._personaCards
      ?.toArray()
      .map((card) => card.getUnsavedChanges?.())
      .filter(Boolean) as Persona[]
    if (payloads.length === 0) return of(void 0)
    return forkJoin(payloads.map((persona) => this._updatePersonaName$(persona.id, persona.name))).pipe(map(() => void 0))
  }

  /**
   * Persists a persona name change from a card (debounced blur path).
   * @param persona { Persona } - The persona to update
   * @returns { void } - The void return type
   */
  public onPersonaNameCommit(persona: Persona): void {
    this._updatePersonaName$(persona.id, persona.name).pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Opens the Manage Persona form-builder shell (fields library). Next continues to legacy editor.
   */
  public openLegacyPersonaEditor(persona: Persona): void {
    this._wizardService.open({
      component: ManagePersonaComponent,
      componentInputs: {
        eventId: this.eventId,
        organizationId: this.organizationId,
        personaId: persona.id,
        eventName: this.eventName,
        personaName: persona.name
      },
      headerConfig: {
        breadcrumbs: [this.eventName, persona.name].filter(Boolean)
      }
    })
  }

  /**
   * Opens the clone persona modal for the selected persona.
   * @param persona { Persona } - The source persona to clone
   */
  public onClonePersona(persona: Persona): void {
    this.cloneSourcePersona.set(persona)
    this._modalService.toggleModal(this.clonePersonaModalId)
  }

  /**
   * Closes the clone persona modal.
   */
  public closeClonePersonaModal(): void {
    if (this._modalService.isModalOpen(this.clonePersonaModalId)) {
      this._modalService.toggleModal(this.clonePersonaModalId)
    }
    this.cloneSourcePersona.set(null)
  }

  /**
   * Adds a successful clone to the wizard list.
   * @param result { PersonaClonedPayload } - Cloned persona and API message
   */
  public onPersonaCloned(result: PersonaClonedPayload): void {
    const clonedPersona = this._normalizePersonaBadgeForList(result.persona)
    this.personas.update((curr) => [...curr.filter((item) => item.id !== clonedPersona.id), clonedPersona])
    this.hasPersonaListMutations.set(true)
    this._alertService.alert(Alert.SUCCESS, result.message)
    this.closeClonePersonaModal()
  }

  /**
   * Archives the selected persona and updates the list.
   * @param persona { Persona } - The persona to archive
   */
  public onArchivePersona(persona: Persona): void {
    this._pendingArchivePersonaId.set(persona.id)
    this.personaNameToArchive.set(persona.name)
    this._modalService.toggleModal(this.confirmArchivePersonaModalId)
  }

  /*
   * @method cancelArchivePersona - cancels the archiving of the persona
   * @returns void
   */
  public cancelArchivePersona(): void {
    if (this._modalService.isModalOpen(this.confirmArchivePersonaModalId)) {
      this._modalService.toggleModal(this.confirmArchivePersonaModalId)
    }
    this._pendingArchivePersonaId.set(null)
    this.personaNameToArchive.set('')
  }

  /*
   * @method confirmArchivePersona - confirms the archiving of the persona
   * @returns void
   */
  public confirmArchivePersona(): void {
    if (this._modalService.isModalOpen(this.confirmArchivePersonaModalId)) {
      this._modalService.toggleModal(this.confirmArchivePersonaModalId)
    }
    const personaId = this._pendingArchivePersonaId()
    const persona = this.personas().find((item) => item.id === personaId)
    if (!persona) return
    this._archivePersona(persona)
  }

  /**
   * Archives the persona and updates the list.
   * @param persona { Persona } - The persona to archive
   */
  private _archivePersona(persona: Persona): void {
    const personaIdsToArchive = this._archivedItemsService.collectLinkedPersonaIdsForRemoval(persona, this.personas())
    const removeSet = new Set(personaIdsToArchive)

    this.personas.update((list) => list.filter((item) => !removeSet.has(item.id)))

    this._archivedItemsService
      .archiveItems(this.organizationId, this.eventId, persona.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          this.hasPersonaListMutations.set(true)
          this._pendingArchivePersonaId.set(null)
          this.personaNameToArchive.set('')
          this._alertService.alert(Alert.SUCCESS, res.message)
        },
        error: (err) => {
          this._pendingArchivePersonaId.set(null)
          this.personaNameToArchive.set('')
          this._load$.next(true)
          const body = err?.error
          const msg =
            typeof body === 'object' && body !== null && 'message' in body && typeof (body as { message: unknown }).message === 'string'
              ? (body as { message: string }).message
              : 'Could not archive persona. Please try again.'
          this._alertService.alert(Alert.ERROR, msg)
        }
      })
  }

  /**
   * Updates the persona name.
   * @param personaId { number } - The persona id
   * @param name { string } - The name to update
   * @returns { Observable<personaPatchResponse> } - The observable to update the persona name
   */
  private _updatePersonaName$(personaId: number, name: string): Observable<personaPatchResponse> {
    return this._personaService.updatePersonaName(this.organizationId, this.eventId, personaId, name).pipe(
      tap((updatedPersona) => {
        this.personas.update((list) =>
          list.map((persona) => {
            if (persona.id !== updatedPersona.data.id) return persona
            const merged = { ...persona, ...updatedPersona.data }
            return this._normalizePersonaBadgeForList(merged)
          })
        )
        this.hasPersonaListMutations.set(true)
        this._alertService.alert(Alert.SUCCESS, updatedPersona.message)
        this._syncBadgeWithPersonaName(updatedPersona.data)
      }),
      catchError((err) => {
        const body = err?.error
        const fieldNameErrors =
          body && typeof body === 'object' && !Array.isArray(body) && 'name' in body && Array.isArray((body as Record<string, unknown>).name)
            ? ((body as Record<string, unknown>).name as unknown[]).filter((m): m is string => typeof m === 'string')
            : []
        if (fieldNameErrors.length) {
          const targetCard = this._personaCards?.toArray().find((card) => card.persona()?.id === personaId)
          targetCard?.revertToSaved()
          this._alertService.alert(Alert.ERROR, fieldNameErrors[0])
          return throwError(() => err)
        }
        const msg =
          typeof body === 'object' && body !== null && 'message' in body && typeof (body as { message: unknown }).message === 'string'
            ? (body as { message: string }).message
            : 'Could not update persona name. Please try again.'
        this._alertService.alert(Alert.ERROR, msg)
        return throwError(() => err)
      })
    )
  }

  /**
   * Mirror the new persona name into its badge HTML and persist via badges/update.
   * @param persona { Persona } - The persona whose badge needs syncing
   */
  private _syncBadgeWithPersonaName(persona: Persona): void {
    this._personaBadgePreviewService
      .syncBadgePersonaName(persona)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          this.personas.update((list) => list.map((p) => (p.id === persona.id ? { ...p, badge: { ...p.badge, ...res.badge } } : p)))
        }
      })
  }

  /**
   * Opens the create persona modal.
   * @returns {void}
   */
  public openCreatePersonaModal(): void {
    this.createPersonaApiError.set(null)
    this._modalService.toggleModal(this.createPersonaModalId)
  }

  /**
   * Cancels the create persona modal.
   * @returns {void}
   */
  public cancelCreatePersonaModal(): void {
    if (this._modalService.isModalOpen(this.createPersonaModalId)) {
      this._modalService.toggleModal(this.createPersonaModalId)
    }
  }

  /**
   * Handles the create persona submission.
   * @param {string} name - The name of the persona to create
   * @returns {void}
   */
  public onCreatePersonaSubmitted(name: string): void {
    if (this.isCreatingPersona()) return

    this.createPersonaApiError.set(null)
    this.isCreatingPersona.set(true)
    this._personaService
      .createPersona(this.organizationId, this.eventId, name)
      .pipe(
        finalize(() => this.isCreatingPersona.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          const data = res.data
          const created = data == null ? undefined : Array.isArray(data) ? data[0] : data
          this._alertService.alert(Alert.SUCCESS, res.message)
          this.createPersonaApiError.set(null)
          if (!created) {
            this._alertService.alert(Alert.ERROR, 'Unexpected response when creating persona.')
            return
          }
          const rendered = this._normalizePersonaBadgeForList(created)
          this.personas.update((curr) => [...curr.filter((persona) => persona.id !== rendered.id), rendered])
          this.hasPersonaListMutations.set(true)
          this.cancelCreatePersonaModal()
        },
        error: (err) => {
          const body = err?.error ?? null
          if (PersonaModel.validationErrorsFromFieldMessages(body, 'name')) {
            this.createPersonaApiError.set(body)
            return
          }
          this.createPersonaApiError.set(null)

          const msg =
            typeof body === 'object' && body !== null && 'message' in body && typeof (body as { message: unknown }).message === 'string'
              ? (body as { message: string }).message
              : 'Could not create persona. Please try again.'
          this._alertService.alert(Alert.ERROR, msg)
        }
      })
  }

  /**
   * Checks if a modal is opened.
   * @param {string} modalId - The id of the modal to check
   * @returns {boolean} - Whether the modal is opened
   */
  public isModalOpened(modalId: string): boolean {
    return this._modalService.isModalOpen(modalId)
  }

  /**
   * Opens landing page setup in the overlay wizard (same as timeline `_redirectToNewLanding`).
   * @param persona { Persona } - The persona to configure the landing page for
   * @returns { void } - The void return type
   */
  public openPersonaLandingPage(persona: Persona): void {
    if (!persona?.id) return
    this._wizardService.open({
      component: LandingPageComponent,
      componentInputs: {
        event_id: this.eventId,
        persona_id: persona.id
      },
      headerConfig: {
        breadcrumbs: [this.eventName, 'Landing Page']
      }
    })
  }
}
